import React, { useState, useEffect, useCallback } from 'react';
import { Agent, Workflow, BillingMetrics, AgentMetricsSummary, MetricsTimeRange, Block } from '../types';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';

// Get the base URL for API documentation (removes /api suffix for public endpoints)
const getPublicApiBaseUrl = () => {
  // In production, use the current domain
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin;
  }
  
  // In development, use localhost or environment variable
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  return apiUrl.replace('/api', '');
};

// Helper function to determine provider from model name
const getProviderFromModel = (model: string): string | null => {
  if (!model) return null;
  
  if (model.startsWith('gpt-') || model.startsWith('o1-') || model.startsWith('text-')) {
    return 'openai';
  }
  if (model.startsWith('claude-')) {
    return 'anthropic';
  }
  if (model.startsWith('gemini-')) {
    return 'google';
  }
  
  return null;
};

// Helper function to get providers used by an agent
const getAgentProviders = (agent: Agent, workflows: Workflow[], blocks: Block[]): string[] => {
  const workflow = workflows.find(w => w.id === agent.workflowId);
  if (!workflow || !workflow.blocks) return [];
  
  const providers = new Set<string>();
  
  // Parse workflow blocks (they might be stored as JSON string or already parsed)
  let workflowBlocks: any[] = [];
  try {
    if (typeof (workflow as any).blocks === 'string') {
      workflowBlocks = JSON.parse((workflow as any).blocks);
    } else if (Array.isArray(workflow.blocks)) {
      workflowBlocks = workflow.blocks;
    }
  } catch (e) {
    console.error('Error parsing workflow blocks:', e);
    return [];
  }
  
  // For each workflow block, find the actual block and check its model
  workflowBlocks.forEach((workflowBlock: any) => {
    if (workflowBlock.config && workflowBlock.config.blockId) {
      const actualBlock = blocks.find(b => b.id === workflowBlock.config.blockId);
      if (actualBlock && actualBlock.model) {
        const provider = getProviderFromModel(actualBlock.model);
        if (provider) {
          providers.add(provider);
        }
      }
    }
  });
  
  return Array.from(providers);
};

// Helper function to generate API key examples for specific providers
const generateApiKeyExamples = (providers: string[]): string => {
  if (providers.length === 0) {
    return `  // Optional: Override admin LLM keys with your own
  // "openai_api_key": "sk-your-openai-key-here",
  // "anthropic_api_key": "sk-ant-your-anthropic-key",
  // "google_api_key": "AIzaYour-Google-API-Key"`;
  }
  
  const examples: string[] = [];
  const comments: string[] = [];
  
  if (providers.includes('openai')) {
    examples.push('  "openai_api_key": "sk-your-openai-key-here"');
  } else {
    comments.push('  // "openai_api_key": "sk-your-openai-key-here"');
  }
  
  if (providers.includes('anthropic')) {
    examples.push('  "anthropic_api_key": "sk-ant-your-anthropic-key"');
  } else {
    comments.push('  // "anthropic_api_key": "sk-ant-your-anthropic-key"');
  }
  
  if (providers.includes('google')) {
    examples.push('  "google_api_key": "AIzaYour-Google-API-Key"');
  } else {
    comments.push('  // "google_api_key": "AIzaYour-Google-API-Key"');
  }
  
  // Add other common providers as comments
  if (!providers.includes('groq')) {
    comments.push('  // "groq_api_key": "gsk_your-groq-key"');
  }
  
  const header = '  // Optional: Override admin LLM keys with your own';
  const allLines = [...examples, ...comments];
  
  return header + '\n' + allLines.join(',\n');
};

// Helper function to generate curl request body for specific providers
const generateCurlBody = (providers: string[]): string => {
  let body = '{\n    "input": "Your text input here"';
  
  // Always show at least one example of how to override admin keys
  if (providers.length > 0) {
    body += ',\n    \n    // Optional: Override admin LLM keys with your own';
    const examples: string[] = [];
    
    if (providers.includes('openai')) {
      examples.push('    "openai_api_key": "sk-your-openai-key-here"');
    }
    if (providers.includes('anthropic')) {
      examples.push('    "anthropic_api_key": "sk-ant-your-anthropic-key"');
    }
    if (providers.includes('google')) {
      examples.push('    "google_api_key": "AIzaYour-Google-API-Key"');
    }
    
    if (examples.length > 0) {
      body += ',\n' + examples.join(',\n');
    }
  }
  
  body += '\n  }';
  return body;
};

const Agents: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentApiKey, setAgentApiKey] = useState<string>('Your Agent API Key from profile');
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'agents' | 'metrics'>('agents');
  
  // Metrics state
  const [billingMetrics, setBillingMetrics] = useState<BillingMetrics | null>(null);
  const [userMetrics, setUserMetrics] = useState<AgentMetricsSummary[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<MetricsTimeRange>('month');
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Form state for creating/editing agents
  const [isEditing, setIsEditing] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    workflowId: '',
    enabled: true,
    cacheEnabled: true,
    cacheTTLMinutes: 1440, // 24 hours default
    cacheMaxEntries: 100,
    cacheCaseSensitive: false
  });
  
  // State for showing API call examples
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [showApiModal, setShowApiModal] = useState(false);

  // State for API testing
  const [testInput, setTestInput] = useState<string>('');
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testStep, setTestStep] = useState<'input' | 'executing' | 'executed' | 'fetching' | 'results'>('input');
  const [testError, setTestError] = useState<string | null>(null);

  const { user } = useAuth();
  const [workflowOwnership, setWorkflowOwnership] = useState<{[key: string]: boolean}>({});

  // Add state for auto-refresh
  const [autoRefreshSeconds, setAutoRefreshSeconds] = useState<number | null>(null);

  // Load metrics data
  const loadMetrics = useCallback(async () => {
    if (!user) return;
    
    setMetricsLoading(true);
    try {
      const [billing, userMetricsData] = await Promise.all([
        api.getBillingMetrics(selectedTimeRange),
        api.getUserMetrics()
      ]);
      
      setBillingMetrics(billing || null);
      setUserMetrics(userMetricsData || []);
    } catch (err) {
      console.error('Error loading metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
      // Set safe defaults on error
      setBillingMetrics(null);
      setUserMetrics([]);
    } finally {
      setMetricsLoading(false);
    }
  }, [user, selectedTimeRange]);

  // Load metrics when tab changes to metrics or time range changes
  useEffect(() => {
    if (activeTab === 'metrics') {
      loadMetrics();
    }
  }, [activeTab, loadMetrics]);

  // Load agents, workflows, and blocks on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [agentsData, workflowsData, blocksData] = await Promise.all([
          api.getAgents(),
          api.getWorkflows(),
          api.getBlocks()
        ]);
        setAgents(agentsData || []);
        setWorkflows(workflowsData || []);
        setBlocks(blocksData || []);
        
        // Check ownership after workflows are loaded
        if (user && workflowsData) {
          const ownershipMap: {[key: string]: boolean} = {};
          workflowsData.forEach(workflow => {
            const workflowUserId = (workflow as any).userId;
            ownershipMap[workflow.id] = workflowUserId === user.id;
          });
          setWorkflowOwnership(ownershipMap);
        }
        
        // Use user's agent API key from the auth context instead of server API key
        if (user?.agentApiKey) {
          setAgentApiKey(user.agentApiKey);
        } else {
          try {
            // If not available in user context, try to fetch it
            const response = await api.auth.getAgentApiKey();
            if (response && response.agentApiKey) {
              setAgentApiKey(response.agentApiKey);
            } else {
              setAgentApiKey('Generate an Agent API Key in your Profile');
            }
          } catch (err) {
            console.error('Error getting agent API key:', err);
            setAgentApiKey('Generate an Agent API Key in your Profile');
          }
        }
        
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
        console.error('Error loading agents:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user?.id, user?.agentApiKey, user]);

  // Make handleFetchResults a useCallback to prevent dependency issues
  const handleFetchResults = useCallback(async () => {
    if (!executionId) return;
    
    setTestStep('fetching');
    setTestError(null);
    setAutoRefreshSeconds(null); // Cancel any existing timer
    
    try {
      const response = await fetch(`${getPublicApiBaseUrl()}/api/public/executions/${executionId}`, {
        headers: {
          'X-API-Key': agentApiKey
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setTestResult(data);
      setTestStep('results');
      
      // Set up auto-refresh if execution is still running
      if (data.status === 'running') {
        setAutoRefreshSeconds(10); // Start a 10-second countdown
      }
    } catch (error) {
      console.error('Error fetching execution results:', error);
      setTestError(error instanceof Error ? error.message : 'Failed to fetch execution results');
      setTestStep('executed'); // Return to the "Fetch Results" step so they can try again
    }
  }, [executionId, agentApiKey]);

  // Modify the autoRefresh useEffect to include handleFetchResults in dependencies
  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null;
    
    if (autoRefreshSeconds !== null && autoRefreshSeconds > 0) {
      timerId = setTimeout(() => {
        setAutoRefreshSeconds(prev => (prev !== null && prev > 0) ? prev - 1 : null);
      }, 1000);
    } else if (autoRefreshSeconds === 0) {
      // Auto-refresh when timer reaches zero
      handleFetchResults();
      setAutoRefreshSeconds(null);
    }
    
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [autoRefreshSeconds, handleFetchResults]);

  const handleCreateAgent = async () => {
    try {
      const newAgent = await api.createAgent(formData);
      setAgents(prevAgents => [...prevAgents, newAgent]);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create agent');
      console.error('Error creating agent:', err);
    }
  };

  const handleUpdateAgent = async () => {
    if (!editingAgentId) return;
    
    try {
      const updatedAgent = await api.updateAgent(editingAgentId, formData);
      setAgents(prevAgents => 
        prevAgents.map(agent => agent.id === editingAgentId ? updatedAgent : agent)
      );
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agent');
      console.error('Error updating agent:', err);
    }
  };

  const handleDeleteAgent = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    
    try {
      await api.deleteAgent(id);
      setAgents(prevAgents => prevAgents.filter(agent => agent.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete agent');
      console.error('Error deleting agent:', err);
    }
  };

  const handleEdit = (agent: Agent) => {
    setFormData({
      name: agent.name,
      description: agent.description,
      workflowId: agent.workflowId,
      enabled: agent.enabled,
      cacheEnabled: (agent as any).cacheEnabled ?? true,
      cacheTTLMinutes: (agent as any).cacheTTLMinutes ?? 1440,
      cacheMaxEntries: (agent as any).cacheMaxEntries ?? 100,
      cacheCaseSensitive: (agent as any).cacheCaseSensitive ?? false
    });
    setEditingAgentId(agent.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      workflowId: '',
      enabled: true,
      cacheEnabled: true,
      cacheTTLMinutes: 1440,
      cacheMaxEntries: 100,
      cacheCaseSensitive: false
    });
    setEditingAgentId(null);
    setIsEditing(false);
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing) {
      handleUpdateAgent();
    } else {
      handleCreateAgent();
    }
  };

  const handleShowApiDocs = (agentId: string) => {
    setSelectedAgentId(agentId);
    setShowApiModal(true);
  };

  const getWorkflowName = (workflowId: string) => {
    if (!workflows) return 'Unknown Workflow';
    const workflow = workflows.find(w => w.id === workflowId);
    return workflow ? workflow.name : 'Unknown Workflow';
  };

  // Function to test agent execution
  const handleTestExecution = async () => {
    if (!selectedAgent || !testInput.trim()) {
      setTestError('Please enter an input message');
      return;
    }

    setTestError(null);
    setTestStep('executing');
    
    try {
      const response = await fetch(`${getPublicApiBaseUrl()}/api/public/agents/${selectedAgent.id}/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': agentApiKey
        },
        body: JSON.stringify({ input: testInput }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setExecutionId(data.executionId);
      setTestStep('executed');
    } catch (error) {
      console.error('Error executing agent:', error);
      setTestError(error instanceof Error ? error.message : 'Failed to execute agent');
      setTestStep('input');
    }
  };

  // Reset the test state
  const resetTest = () => {
    setTestInput('');
    setExecutionId(null);
    setTestResult(null);
    setTestStep('input');
    setTestError(null);
    setAutoRefreshSeconds(null); // Cancel any auto-refresh timer
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const selectedAgent = selectedAgentId && agents ? 
    agents.find(a => a.id === selectedAgentId) : null;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Agents & Metrics</h1>
        {activeTab === 'agents' && (
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : 'Create Agent'}
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6">
        <button
          onClick={() => setActiveTab('agents')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            activeTab === 'agents'
              ? 'bg-blue-100 text-blue-700 border border-blue-300'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          Agents
        </button>
        <button
          onClick={() => setActiveTab('metrics')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            activeTab === 'metrics'
              ? 'bg-blue-100 text-blue-700 border border-blue-300'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          Metrics & Billing
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
        </div>
      )}

      {/* Agents Tab */}
      {activeTab === 'agents' && (
        <div>

      {showForm && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium mb-4">{isEditing ? 'Edit Agent' : 'Create Agent'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  id="name"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label htmlFor="workflowId" className="block text-sm font-medium text-gray-700 mb-1">Workflow</label>
                <select
                  id="workflowId"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={formData.workflowId}
                  onChange={(e) => setFormData({ ...formData, workflowId: e.target.value })}
                  required
                >
                  <option value="">Select a workflow</option>
                  {workflows.map(workflow => (
                    <option 
                      key={workflow.id} 
                      value={workflow.id}
                      className={workflowOwnership[workflow.id] === false ? 'bg-red-100' : ''}
                    >
                      {workflow.name} 
                      {workflowOwnership[workflow.id] === false ? ' (⚠️ Not yours)' : ''}
                    </option>
                  ))}
                </select>
                
                {formData.workflowId && workflowOwnership[formData.workflowId] === false && (
                  <div className="mt-1 text-sm text-red-600">
                    ⚠️ Warning: This workflow appears to belong to another user. Using it will cause a "Access denied" error.
                  </div>
                )}
                
                {workflows.length === 0 && (
                  <div className="mt-1 text-sm text-amber-600">
                    You need to create a workflow first before creating an agent.
                  </div>
                )}
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  id="description"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                  <span className="ml-2 text-sm text-gray-700">Enabled</span>
                </label>
              </div>
            </div>
            
            <div className="md:col-span-2 border-t pt-4">
              <h3 className="text-md font-medium text-gray-900 mb-4">Cache Configuration</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                      checked={formData.cacheEnabled}
                      onChange={(e) => setFormData({ ...formData, cacheEnabled: e.target.checked })}
                    />
                    <span className="ml-2 text-sm text-gray-700">Enable Caching</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Cache responses to reduce API costs and improve performance</p>
                </div>
                
                <div>
                  <label htmlFor="cacheTTL" className="block text-sm font-medium text-gray-700 mb-1">Cache TTL (minutes)</label>
                  <input
                    type="number"
                    id="cacheTTL"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formData.cacheTTLMinutes}
                    onChange={(e) => setFormData({ ...formData, cacheTTLMinutes: parseInt(e.target.value) || 0 })}
                    min="0"
                    placeholder="1440 (24 hours)"
                    disabled={!formData.cacheEnabled}
                  />
                  <p className="mt-1 text-xs text-gray-500">How long to keep cached responses (0 = use default)</p>
                </div>
                
                <div>
                  <label htmlFor="cacheMaxEntries" className="block text-sm font-medium text-gray-700 mb-1">Max Cache Entries</label>
                  <input
                    type="number"
                    id="cacheMaxEntries"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={formData.cacheMaxEntries}
                    onChange={(e) => setFormData({ ...formData, cacheMaxEntries: parseInt(e.target.value) || 0 })}
                    min="0"
                    placeholder="100"
                    disabled={!formData.cacheEnabled}
                  />
                  <p className="mt-1 text-xs text-gray-500">Maximum number of cached responses (0 = use default)</p>
                </div>
                
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                      checked={formData.cacheCaseSensitive}
                      onChange={(e) => setFormData({ ...formData, cacheCaseSensitive: e.target.checked })}
                      disabled={!formData.cacheEnabled}
                    />
                    <span className="ml-2 text-sm text-gray-700">Case Sensitive</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Whether cache keys are case sensitive</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {isEditing ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workflow</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cache</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {agents.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                  No agents found. Create one to get started.
                </td>
              </tr>
            ) : (
              agents.map(agent => (
                <tr key={agent.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{agent.name}</div>
                    <div className="text-sm text-gray-500">{agent.description}</div>
                  </td>
                  <td className="px-6 py-4 max-w-md">
                    <div className="text-sm text-gray-900 break-words">{getWorkflowName(agent.workflowId)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${agent.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {agent.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        (agent as any).cacheEnabled !== false
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {(agent as any).cacheEnabled !== false ? 'On' : 'Off'}
                      </span>
                      {(agent as any).cacheEnabled !== false && (
                        <span className="text-xs text-gray-500">
                          {(agent as any).cacheTTLMinutes || 1440}m
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(agent.createdAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white">
                    <button
                      onClick={() => handleShowApiDocs(agent.id)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                    >
                      API
                    </button>
                    <button
                      onClick={() => handleEdit(agent)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAgent(agent.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* API Docs Modal */}
      {showApiModal && selectedAgent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      API Documentation for: {selectedAgent.name}
                    </h3>
                    
                    {/* LLM API Key Override Information */}
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2">🔑 LLM API Key Override</h4>
                      <p className="text-sm text-blue-700 mb-3">
                        You can override admin-configured LLM provider keys by including your own API keys in the request. 
                        This gives you full control over costs and usage.
                      </p>
                      <div className="text-xs text-blue-600">
                        <p className="mb-1"><strong>✅ Supported formats:</strong></p>
                        <ul className="list-disc list-inside ml-2 mb-2">
                          <li>Individual fields: <code className="bg-blue-100 px-1 rounded">openai_api_key</code>, <code className="bg-blue-100 px-1 rounded">google_api_key</code>, etc.</li>
                          <li>Object format: <code className="bg-blue-100 px-1 rounded">llm_api_keys: {"{openai: 'sk-...', google: 'AIza...'}"}</code></li>
                        </ul>
                        <p className="mb-1"><strong>🛡️ Security:</strong> When you provide API keys, the system uses ONLY your keys (no fallback to admin keys).</p>
                        <p><strong>💰 Billing:</strong> Your API usage will be charged to your own provider accounts.</p>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <h4 className="text-md font-medium mb-2">Execute Agent API</h4>
                      <div className="bg-gray-100 p-3 rounded-md font-mono text-sm mb-3">
                        POST {getPublicApiBaseUrl()}/api/public/agents/{selectedAgent.id}/execute
                      </div>
                      
                      <h5 className="text-sm font-medium mb-1">Headers:</h5>
                      <div className="bg-gray-100 p-3 rounded-md font-mono text-xs mb-3">
                        Content-Type: application/json<br />
                        X-API-Key: {agentApiKey}
                      </div>
                      
                      <h5 className="text-sm font-medium mb-1">Request Body:</h5>
                      <div className="bg-gray-100 p-3 rounded-md font-mono text-xs mb-3">
                        {`{
  "input": "Your text input here",
  
${generateApiKeyExamples(getAgentProviders(selectedAgent, workflows, blocks))}
}`}
                      </div>
                      
                      <h5 className="text-sm font-medium mb-1">Sample curl:</h5>
                      <div className="bg-gray-100 p-3 rounded-md font-mono text-xs overflow-x-auto whitespace-pre">
{`curl -X POST \\
  "${getPublicApiBaseUrl()}/api/public/agents/${selectedAgent.id}/execute" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${agentApiKey}" \\
  -d '${generateCurlBody(getAgentProviders(selectedAgent, workflows, blocks))}'`}
                      </div>
                      
                      <h5 className="text-sm font-medium mt-4 mb-1">Get Results API:</h5>
                      <div className="bg-gray-100 p-3 rounded-md font-mono text-xs mb-3">
                        GET {getPublicApiBaseUrl()}/api/public/executions/[executionId]
                      </div>
                      
                      <h5 className="text-sm font-medium mb-1">Sample curl:</h5>
                      <div className="bg-gray-100 p-3 rounded-md font-mono text-xs overflow-x-auto whitespace-pre">
{`curl -X GET \\
  "${getPublicApiBaseUrl()}/api/public/executions/[executionId]" \\
  -H "X-API-Key: ${agentApiKey}"`}
                      </div>

                      {/* Supported LLM Provider Fields */}
                      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-md p-4">
                        <h5 className="text-sm font-semibold text-gray-800 mb-3">📋 Supported LLM Provider Override Fields</h5>
                        <div className="text-xs">
                          <p className="font-medium text-gray-700 mb-2">Supported Providers:</p>
                          <ul className="text-gray-600 space-y-1 ml-4">
                            <li><code className="bg-gray-100 px-2 py-0.5 rounded">openai_api_key</code> - OpenAI</li>
                            <li><code className="bg-gray-100 px-2 py-0.5 rounded">anthropic_api_key</code> - Anthropic</li>
                            <li><code className="bg-gray-100 px-2 py-0.5 rounded">google_api_key</code> - Google</li>
                            <li><code className="bg-gray-100 px-2 py-0.5 rounded">openrouter_api_key</code> - OpenRouter</li>
                          </ul>
                        </div>
                        <div className="mt-3 text-xs text-gray-600">
                          <p className="mb-1"><strong>Alternative format:</strong> Use <code className="bg-gray-100 px-1 rounded">llm_api_keys</code> object with provider names as keys.</p>
                          <p><strong>Priority:</strong> Individual fields → llm_api_keys object → admin keys (only if no user keys provided)</p>
                        </div>
                      </div>

                      {/* Test API Section */}
                      <div className="mt-8 border-t pt-4">
                        <div className="flex justify-between items-center">
                          <h4 className="text-md font-medium">Test Agent API</h4>
                          <button 
                            onClick={resetTest}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Reset
                          </button>
                        </div>
                        
                        {testError && (
                          <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm mt-3 mb-4">
                            {testError}
                          </div>
                        )}

                        {/* Step 1: Input and Execute */}
                        {testStep === 'input' && (
                          <div className="mt-4">
                            <h5 className="text-sm font-medium mb-2">1. Enter your input and execute:</h5>
                            <textarea
                              className="w-full p-2 border rounded-md mb-3 font-mono text-sm"
                              rows={3}
                              placeholder="Enter your message to the agent"
                              value={testInput}
                              onChange={(e) => setTestInput(e.target.value)}
                            />
                            <button
                              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                              onClick={handleTestExecution}
                            >
                              Execute Agent
                            </button>
                          </div>
                        )}

                        {/* Step 2: Executing */}
                        {testStep === 'executing' && (
                          <div className="mt-4">
                            <div className="flex items-center text-blue-700">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                              Executing agent...
                            </div>
                          </div>
                        )}

                        {/* Step 3: Execution Complete */}
                        {testStep === 'executed' && (
                          <div className="mt-4">
                            <h5 className="text-sm font-medium mb-2">2. Execution started:</h5>
                            <div className="bg-green-50 p-3 rounded-md text-sm mb-4">
                              <p>Agent execution started successfully!</p>
                              <p className="mt-1"><strong>Execution ID:</strong> <span className="font-mono">{executionId}</span></p>
                            </div>
                            <button
                              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                              onClick={handleFetchResults}
                            >
                              Fetch Results
                            </button>
                          </div>
                        )}

                        {/* Step 4: Fetching Results */}
                        {testStep === 'fetching' && (
                          <div className="mt-4">
                            <div className="flex items-center text-blue-700">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                              Fetching execution results...
                            </div>
                          </div>
                        )}

                        {/* Step 5: Results */}
                        {testStep === 'results' && testResult && (
                          <div className="mt-4">
                            <h5 className="text-sm font-medium mb-2">3. Execution Results:</h5>
                            <div className="bg-gray-100 p-3 rounded-md overflow-x-auto mb-3">
                              <div className="flex mb-2">
                                <span className="font-medium w-24">Status:</span>
                                <span className={`px-2 text-xs leading-5 font-semibold rounded-full ${
                                  testResult.status === 'completed' ? 'bg-green-100 text-green-800' : 
                                  testResult.status === 'running' ? 'bg-blue-100 text-blue-800' : 
                                  testResult.status === 'failed' ? 'bg-red-100 text-red-800' : 
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {testResult.status || 'unknown'}
                                </span>
                              </div>
                              
                              {testResult.status === 'running' && (
                                <div className="flex items-center text-blue-700 text-xs mt-1 mb-2">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700 mr-2"></div>
                                  <span>
                                    Execution is still processing. 
                                    {autoRefreshSeconds !== null 
                                      ? ` Refreshing in ${autoRefreshSeconds} seconds...` 
                                      : ' You can click "Fetch Again" to check for updates.'}
                                  </span>
                                </div>
                              )}
                              
                              {testResult.error && (
                                <div className="flex mb-2">
                                  <span className="font-medium w-24">Error:</span>
                                  <span className="text-red-600">{testResult.error}</span>
                                </div>
                              )}
                              
                              <div className="mt-4">
                                <h6 className="text-xs font-medium mb-1">Full Response:</h6>
                                <pre className="text-xs overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">
                                  {JSON.stringify(testResult, null, 2)}
                                </pre>
                              </div>
                            </div>
                            
                            <div className="flex space-x-3">
                              {testResult.status !== 'completed' && (
                                <button
                                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center"
                                  onClick={handleFetchResults}
                                >
                                  {autoRefreshSeconds !== null ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                                      Refresh Now
                                    </>
                                  ) : (
                                    'Fetch Again'
                                  )}
                                </button>
                              )}
                              <button
                                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
                                onClick={resetTest}
                              >
                                Start New Test
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setShowApiModal(false);
                    setSelectedAgentId(null);
                    resetTest();
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      )}

      {/* Metrics Tab */}
      {activeTab === 'metrics' && (
        <div>
          {metricsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Time Range Selector */}
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">Billing Overview</h2>
                <select
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value as MetricsTimeRange)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="year">This Year</option>
                </select>
              </div>

              {/* Billing Metrics Cards */}
              {billingMetrics && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Total API Calls */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total API Calls</p>
                        <p className="text-2xl font-semibold text-gray-900">{(billingMetrics.totalApiCalls || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Cache Hit Rate */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Cache Hit Rate</p>
                        <p className="text-2xl font-semibold text-gray-900">{((billingMetrics.cacheHitRate || 0) * 100).toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>

                  {/* Total Cost */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total Cost</p>
                        <p className="text-2xl font-semibold text-gray-900">${(billingMetrics.totalCostUsd || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Estimated Savings */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Est. Savings</p>
                        <p className="text-2xl font-semibold text-gray-900">${(billingMetrics.estimatedSavingsUsd || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Token Usage */}
              {billingMetrics && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Token Usage</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Input Tokens</p>
                      <p className="text-xl font-semibold text-blue-600">{(billingMetrics.totalInputTokens || 0).toLocaleString()}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Output Tokens</p>
                      <p className="text-xl font-semibold text-green-600">{(billingMetrics.totalOutputTokens || 0).toLocaleString()}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Total Tokens</p>
                      <p className="text-xl font-semibold text-gray-900">{(billingMetrics.totalTokens || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Agent Performance Table */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Agent Performance</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calls</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cache Hit Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Tokens</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {!userMetrics || userMetrics.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                            No metrics data available for the selected period.
                          </td>
                        </tr>
                      ) : (
                        userMetrics.map((metric) => (
                          <tr key={metric.agentId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{metric.agentName}</div>
                              <div className="text-sm text-gray-500">{metric.agentId}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{metric.totalCalls || 0}</div>
                              <div className="text-xs text-gray-500">
                                {metric.cachedCalls || 0} cached, {metric.uniqueCalls || 0} unique
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="text-sm text-gray-900">{((metric.cacheHitRate || 0) * 100).toFixed(1)}%</div>
                                <div className="ml-2 flex-1 bg-gray-200 rounded-full h-2 max-w-20">
                                  <div
                                    className="bg-blue-600 h-2 rounded-full"
                                    style={{ width: `${(metric.cacheHitRate || 0) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {((metric.totalInputTokens || 0) + (metric.totalOutputTokens || 0)).toLocaleString()}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(metric.totalInputTokens || 0).toLocaleString()} in, {(metric.totalOutputTokens || 0).toLocaleString()} out
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">${(metric.totalCostUsd || 0).toFixed(2)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{metric.avgExecutionTime || 0}ms</div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top Agents by Cost */}
              {billingMetrics && billingMetrics.topAgents && billingMetrics.topAgents.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Top Agents by Cost</h3>
                  <div className="space-y-3">
                    {billingMetrics.topAgents.map((agent, index) => (
                      <div key={agent.agentId} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{agent.agentName || 'Unknown Agent'}</p>
                            <p className="text-xs text-gray-500">{agent.calls || 0} calls</p>
                          </div>
                        </div>
                        <div className="text-sm font-medium text-gray-900">${(agent.costUsd || 0).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Agents; 